import classNames from 'classnames'

import {ALL_STATUS, MO_STATII} from '../../common/constants/MOs.js'
import {Plural, PluralBlock} from '../../common/components/Plural.js'
import FormattedDate from '../../common/components/FormattedDate.js'
import RowAddress from '../../common/components/List/RowAddress.js'
import Checkbox from '../../common/components/Checkbox.js'
import ButtonLink from '../../common/components/Button/ButtonLink.js'
import TimelinePopover from '../../common/components/List/TimelinePopover/index.js'
import Quantity from '../../common/components/Quantity.js'
import {productsSelector, getProductName} from '../../data/products.js'
import {
  warehousesSelector,
  getWarehouseName,
  getWarehouseAddress,
} from '../../data/warehouses.js'
import {
  createMOSelector,
  moStatusSelector,
  createMOCommentGroupsSelector,
  createMOLinesSelector,
  createMOTotalSKUCountSelector,
  createMOTotalLineQuantitySelector,
  createMOCreatedDateSelector,
  createMOUpdatedDateSelector,
  createMOTagsSelector,
  moWarehouseIDSelector,
  manufacturerNotesSelector,
  internalNotesSelector,
  createMOTotalFinishedSelector,
  ensureMOComments,
} from '../../data/mos.js'
import {
  showMOManufacturerNotesModal,
  showMOInternalNotesModal,
  showMOWarehouseModal,
} from './Modals/EditMOParamModal.js'
import {moListSelectReferenceID, moListToggleExpanded} from './moListActions.js'
import {
  moListQuerySelector,
  createIsSelectedSelector,
  createIsExpandedSelector,
  createDetailURLSelector,
} from './moListSelectors.js'
import MOTags from './MOTags.js'
import {useSelector} from '../../store.js'
import {useMemo} from 'react'
import Link from '../../common/components/Link/Link.js'
import Icon from '../../common/icons/Icon.js'

/**
 * MO Row component for displaying a single Manufacturing Order (MO) in the list.
 *
 * @param {Object} props - The component props.
 * @param {String} props.referenceID - The reference ID of the MO.
 *
 * @returns {React.ReactElement} The rendered component.
 */
export default function Row({referenceID}) {
  const [
    commentGroupsSelector,
    isSelectedSelector,
    isExpandedSelector,
    detailURLSelector,
    linesSelector,
    totalSKUCountSelector,
    totalLineQuantitySelector,
    totalFinishedSelector,
    createdDateSelector,
    updatedDateSelector,
    tagsSelector,
  ] = useMemo(() => {
    const moSelector = createMOSelector(referenceID)

    return [
      createMOCommentGroupsSelector(moSelector),
      createIsSelectedSelector(referenceID),
      createIsExpandedSelector(referenceID),
      createDetailURLSelector(referenceID),
      createMOLinesSelector(moSelector),
      createMOTotalSKUCountSelector(moSelector),
      createMOTotalLineQuantitySelector(moSelector),
      createMOTotalFinishedSelector(moSelector),
      createMOCreatedDateSelector(moSelector),
      createMOUpdatedDateSelector(moSelector),
      createMOTagsSelector(moSelector),
    ]
  }, [referenceID])

  const status = useSelector(moListQuerySelector).status
  const isSelected = useSelector(isSelectedSelector)
  const isExpanded = useSelector(isExpandedSelector)
  const detailURL = useSelector(detailURLSelector)
  const lines = useSelector(linesSelector)
  const totalSKUCount = useSelector(totalSKUCountSelector)
  const totalLineQuantity = useSelector(totalLineQuantitySelector)
  const totalFinished = useSelector(totalFinishedSelector)
  const createdDate = useSelector(createdDateSelector)
  const updatedDate = useSelector(updatedDateSelector)
  const tags = useSelector(tagsSelector)
  const warehousesByID = useSelector(warehousesSelector)
  const productsBySKU = useSelector(productsSelector)
  const internalNotes = useSelector((state) =>
    internalNotesSelector(state, {referenceID}),
  )
  const manufacturerNotes = useSelector((state) =>
    manufacturerNotesSelector(state, {referenceID}),
  )
  const moStatus = useSelector((state) =>
    moStatusSelector(state, {referenceID}),
  )
  const warehouseID = useSelector((state) =>
    moWarehouseIDSelector(state, {referenceID}),
  )

  const warehouse = warehousesByID[warehouseID]

  return (
    <tr
      className={classNames('table__tr table__tr--mo-row', {
        'table__tr--expanded': isExpanded,
      })}
    >
      <td className="table__td table__td--order-checkbox relative">
        <TimelinePopover
          title="MO Timeline"
          type="mo"
          commentGroupsSelector={commentGroupsSelector}
          ensureComments={() => ensureMOComments(referenceID)}
        />
        <Checkbox
          checked={isSelected}
          onClick={(checked, event) =>
            moListSelectReferenceID(referenceID, checked, event.shiftKey)
          }
        />
      </td>
      <td className="table__td">
        {status === ALL_STATUS &&
          (() => {
            const {badge, name} =
              MO_STATII.find(({slug}) => slug === moStatus) || {}

            return (
              <div
                className="status-badge inline-block lh-md tooltip--top margin-right-5 v-align-top"
                data-hint={name}
              >
                <strong>{badge}</strong>
              </div>
            )
          })()}
        <div className="wrap--product-name inline-block">
          <a
            className="btn btn--link darkest fs-n0 lh-md margin-right-5"
            href={detailURL}
          >
            <strong>{referenceID}</strong>
          </a>
        </div>
        {tags.length > 0 && (
          <div
            className={
              isExpanded
                ? 'wrap--po-addl-info list list--order-data'
                : 'wrap--summary-tags-table wrap--summary-tags-inline'
            }
          >
            <MOTags referenceID={referenceID} tags={tags} />
          </div>
        )}
        <dl className="wrap--po-addl-info list list--order-data with-top-divider">
          <dt className="list__title--order-data">Notes for Manufacturer</dt>
          {manufacturerNotes ? (
            <dd className="list__item--order-data">
              <div className="ws-pre-wrap">{manufacturerNotes}</div>
              <ButtonLink
                className="meta-molist-button-edit-internal-note"
                onClick={() => showMOManufacturerNotesModal(referenceID)}
              >
                Edit
              </ButtonLink>
            </dd>
          ) : (
            <dd className="list__item--order-data">
              <ButtonLink
                className="meta-molist-button-add-internal-note"
                onClick={() => showMOManufacturerNotesModal(referenceID)}
              >
                Add note
              </ButtonLink>
            </dd>
          )}
        </dl>

        <dl className="wrap--po-addl-info list list--order-data with-top-divider">
          <dt className="list__title--order-data">Internal Notes</dt>
          {internalNotes ? (
            <dd className="list__item--order-data">
              <div className="ws-pre-wrap">{internalNotes}</div>
              <ButtonLink
                className="meta-molist-button-edit-internal-note"
                onClick={() => showMOInternalNotesModal(referenceID)}
              >
                Edit
              </ButtonLink>
            </dd>
          ) : (
            <dd className="list__item--order-data">
              <ButtonLink
                className="meta-molist-button-add-internal-note"
                onClick={() => showMOInternalNotesModal(referenceID)}
              >
                Add note
              </ButtonLink>
            </dd>
          )}
        </dl>
      </td>
      <td className="table__td">
        {isExpanded ? (
          <table className="table table--list-page-nested">
            <thead>
              <tr>
                <th className="table__th table__th--sm w-40">
                  <div className="fs-n0 lh-md">
                    <PluralBlock count={totalSKUCount}>
                      <strong>
                        <Quantity value={totalSKUCount} /> <Plural word="SKU" />
                      </strong>{' '}
                    </PluralBlock>
                  </div>
                </th>
                <th className="table__th table__th--sm align-right w-30">
                  <div className="fs-n0 lh-md">
                    Requested{' '}
                    {totalSKUCount > 1 && (
                      <span className="margin-right-n03">
                        (<Quantity value={totalLineQuantity} />)
                      </span>
                    )}
                  </div>
                </th>
                <th className="table__th table__th--sm align-right w-30">
                  <div className="fs-n0 lh-md">
                    Finished{' '}
                    {totalSKUCount > 1 && (
                      <span className="margin-right-n03">
                        (<Quantity value={totalFinished} />)
                      </span>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.id}>
                  <td className="table__td table__td--sm">
                    <Link
                      className="fs-n0 lh-md"
                      href={`#/product/${encodeURIComponent(line.sku)}/bom`}
                    >
                      <div>{getProductName(productsBySKU[line.sku])}</div>
                      <div className="margin-bottom-5">{line.sku}</div>
                    </Link>
                  </td>
                  <td className="table__td table__td--sm align-right">
                    <span className="fs-00 lh-md">
                      <Quantity value={line.requested_quantity} />
                    </span>
                  </td>
                  <td className="table__td table__td--sm align-right">
                    <span className="fs-00 lh-md">
                      <Quantity value={line.finished_quantity} />
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="fs-n0 lh-md flex">
            <PluralBlock count={totalSKUCount}>
              <div>
                <strong>
                  {totalSKUCount} <Plural word="SKU" />
                </strong>{' '}
              </div>
            </PluralBlock>
            <span className="op-50 margin-left-7 margin-right-7">–</span>
            <div className="op-75 border-right--light padding-right-10 margin-right-10">
              <strong>
                <Quantity value={totalLineQuantity} />
              </strong>{' '}
              Requested
            </div>
            <div className="op-75">
              <strong>
                <Quantity value={totalFinished} />
              </strong>{' '}
              Finished
            </div>
          </div>
        )}
      </td>

      <td className="table__td">
        <div>
          <strong className="fs-n0 lh-md">{getWarehouseName(warehouse)}</strong>
        </div>
        <div className="wrap--po-addl-info divider--top">
          <RowAddress
            address={getWarehouseAddress(warehouse)}
            noPhone
            noEmail
          />
        </div>
        <ButtonLink
          className="meta-returnorderlist-button-edit-warehouse"
          onClick={() => showMOWarehouseModal(referenceID)}
        >
          Edit
        </ButtonLink>
      </td>
      <td className="table__td">
        <div className="fs-n0 lh-md margin-bottom-1">
          <FormattedDate value={createdDate} format="MMM D, YYYY" />
          {' at '}
          <FormattedDate value={createdDate} format="h:mma" />
        </div>
        <em className="fs-n2 text--lt-grey">
          Updated: <FormattedDate value={updatedDate} format="MMM D, YYYY" />
          {' at '}
          <FormattedDate value={updatedDate} format="h:mma" />
        </em>
      </td>

      <td className="table__td align-right">
        <ButtonLink
          className="no-underline btn--expand-product-row"
          onClick={() => moListToggleExpanded(referenceID)}
        >
          <Icon name="expand" className="fs-01 lh-0 v-align-middle" />
        </ButtonLink>
      </td>
    </tr>
  )
}
